import React, {
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {SimpleTreeView, TreeItem, TreeViewBaseItem} from '@mui/x-tree-view';
import {Typography} from '@mui/material';

import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ArrMenuHandle, CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import ArrTooltip from '../../common/arr-tooltip/ArrTooltip';

export type TrackItemsType = {
  label: string;
  id: string;
};

interface IDocExplorerLeftTreeViewProps {
  itemsList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  setSelectedItem?: (
    value: string[] | null,
    isCtrlPressed?: boolean,
    node?: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => void;
  selectedItem?: string;
  treeSelectedItems?: string[] | null;
  multipleSelection?: boolean;
  isShowCountLabel?: boolean;
  handleNoResultMessage?: string;
  allowGlobalSelect?: boolean;
  expandedNodes?: string[];
  collapsedNodes?: string[];
  onExpandedNodesChange?: (expandedNodes: string[]) => void;
  onCollapsedNodesChange?: (collapsedNodes: string[]) => void;
  setExpandedFolders?: (data: string[]) => void;
  showActions?: boolean;
  showCheckmark?: boolean;
  selectedNodeToMove?: string;
  updateTreeData?: React.Dispatch<
    React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>
  >;
  isShowCheckbox?: boolean;
  MoreMenuOptions?: CustomMenuItem[];
  onMenuItemClick?: (item: CustomMenuItem) => void;
  customContentClass?: string;
  customContentHeight?: number;
  onNodeExpand?: (
    nodeId: string
  ) => Promise<TreeViewBaseItem<ExtendedTreeItemProps>[]>;
  onNodeCollapse?: (nodeId: string) => void;
  loadingNodes?: Set<string>;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

const ROW_HEIGHT_PX = 36;
const VIRTUALIZATION_THRESHOLD = 80;
const OVERSCAN = 15;

export type FlattenedTreeRow = {
  id: string;
  depth: number;
  node: TreeViewBaseItem<ExtendedTreeItemProps>;
};

function flattenTreeToVisibleRows(
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  expandedIdsSet: Set<string>,
  depth = 0
): FlattenedTreeRow[] {
  const newFolders = nodes.filter(
    (node) => node.fileType === 'folder' && node.newFolder
  );
  const folders = nodes.filter(
    (node) => node.fileType === 'folder' && !node.newFolder
  );
  const files = nodes.filter((node) => node.fileType !== 'folder');
  const ordered = [...newFolders, ...folders, ...files];
  const result: FlattenedTreeRow[] = [];
  for (const node of ordered) {
    result.push({id: node.id, depth, node});
    const isExpanded = expandedIdsSet.has(node.id);
    const hasVisibleChildren =
      ((node.isChildrenLoaded && node.hasChildren) ||
        (node.hasChildren && node.children && node.children.length > 0)) &&
      node.children &&
      node.children.length > 0;
    if (isExpanded && hasVisibleChildren && node.children) {
      result.push(
        ...flattenTreeToVisibleRows(node.children, expandedIdsSet, depth + 1)
      );
    }
  }
  return result;
}

interface TreeItem {
  id: string;
  label: string;
  fileType: string;
  children?: TreeItem[];
}

const DocExplorerLeftTreeView = (props: IDocExplorerLeftTreeViewProps) => {
  const {
    itemsList,
    setSelectedItem,
    selectedItem,
    multipleSelection,
    isShowCountLabel = true,
    handleNoResultMessage,
    expandedNodes = [],
    onExpandedNodesChange,
    onCollapsedNodesChange,
    showActions = false,
    showCheckmark = false,
    selectedNodeToMove,
    updateTreeData,
    isShowCheckbox = false,
    MoreMenuOptions,
    onMenuItemClick,
    customContentHeight,
    customContentClass = '',
    onNodeExpand,
    scrollContainerRef,
  } = props;

  const [selectedItems, setSelectedItems] = useState<
    string | string[] | null | undefined
  >(selectedItem);
  const [treeData, setTreeData] = useState(itemsList);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>(expandedNodes);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const menuRef1 = useRef<ArrMenuHandle | null>(null);

  useEffect(() => {
    if (updateTreeData) {
      updateTreeData(treeData);
    }
  }, [treeData]);

  useEffect(() => {
    setTreeData(itemsList);
    if (itemsList.length == 1 && !expandedNodes.length) {
      const id = itemsList.map((item) => item.id);
      setExpandedIds(id);
    }
  }, [itemsList]);

  const expandedIdsSet = React.useMemo(
    () => new Set(expandedIds),
    [expandedIds]
  );

  const flattenedRows = React.useMemo(
    () => flattenTreeToVisibleRows(treeData, expandedIdsSet),
    [treeData, expandedIdsSet]
  );

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const onResize = () =>
      setContainerHeight(el.getBoundingClientRect().height);
    onResize();
    el.addEventListener('scroll', onScroll, {passive: true});
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [scrollContainerRef]);

  const useVirtualization =
    !!scrollContainerRef && flattenedRows.length >= VIRTUALIZATION_THRESHOLD;

  const {startIndex, endIndex, totalHeight} = React.useMemo(() => {
    const totalHeightVal = flattenedRows.length * ROW_HEIGHT_PX;
    if (!useVirtualization) {
      return {
        startIndex: 0,
        endIndex: Math.max(0, flattenedRows.length - 1),
        totalHeight: totalHeightVal,
      };
    }
    const effectiveHeight = containerHeight > 0 ? containerHeight : 400;
    const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT_PX) - OVERSCAN);
    const visibleCount = Math.ceil(effectiveHeight / ROW_HEIGHT_PX);
    const end = Math.min(
      flattenedRows.length - 1,
      start + visibleCount + OVERSCAN * 2
    );
    return {
      startIndex: start,
      endIndex: end,
      totalHeight: totalHeightVal,
    };
  }, [useVirtualization, scrollTop, containerHeight, flattenedRows.length]);

  const handleSelectedItemsChange = (
    event: SyntheticEvent<Element, Event> | null,
    ids: string[] | string | null
  ) => {
    const isCtrlPressed = Boolean(
      !!event &&
        (('ctrlKey' in event && event.ctrlKey) ||
          ('nativeEvent' in event &&
            'ctrlKey' in event.nativeEvent &&
            (event.nativeEvent as MouseEvent | KeyboardEvent).ctrlKey))
    );

    const clickedIds = Array.isArray(ids) ? ids : ids ? [ids] : [];
    const clickedId = clickedIds[0];

    if (!clickedId) return;

    const selectedNode = findNodeById(treeData, clickedId);
    if (!selectedNode || selectedNode.disabled) return;

    if (showCheckmark) {
      const parentFolderId = findParentById(treeData, selectedNodeToMove ?? '');

      if (
        clickedId === selectedNodeToMove ||
        clickedId === parentFolderId ||
        selectedNode.fileType !== 'folder'
      ) {
        return;
      }
    }

    const isValidId = (id: string) =>
      id && !id.includes('loading') && id !== selectedNodeToMove;

    if (!isValidId(clickedId)) return;

    if (isCtrlPressed) {
      let newSelection = Array.isArray(selectedItems)
        ? [...selectedItems]
        : selectedItems
          ? [selectedItems?.toString()]
          : [];

      if (newSelection.includes(clickedId)) {
        newSelection = newSelection.filter((id) => id !== clickedId);
      } else {
        newSelection.push(clickedId);
      }

      setSelectedItems(newSelection);
      if (setSelectedItem)
        setSelectedItem(newSelection, isCtrlPressed, selectedNode);
    } else if (multipleSelection) {
      let newSelection = Array.isArray(selectedItems) ? [...selectedItems] : [];

      if (newSelection.includes(clickedId)) {
        newSelection = newSelection.filter((id) => id !== clickedId);
      } else {
        newSelection.push(clickedId);
      }

      setSelectedItems(newSelection);
      if (setSelectedItem)
        setSelectedItem(newSelection, isCtrlPressed, selectedNode);
    } else {
      setSelectedItems(clickedId);
      if (setSelectedItem)
        setSelectedItem([clickedId], isCtrlPressed, selectedNode);
    }
  };

  const isNodeSelected = (nodeId: string) => {
    if (Array.isArray(selectedItems)) {
      return selectedItems.includes(nodeId);
    }
    return selectedItems === nodeId;
  };

  const getAllChildNodeIds = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ): string[] => {
    if (!node.children) return [];
    return node.children.reduce<string[]>(
      (acc, child) => [...acc, child.id, ...getAllChildNodeIds(child)],
      []
    );
  };

  const handleCheckboxSelection = (checked: boolean, nodeId: string) => {
    const node = findNodeById(treeData, nodeId);
    if (!node) return;

    const childIds = getAllChildNodeIds(node);
    const nodeIdsToChange = [nodeId, ...childIds];

    setSelectedItems((prev) => {
      const current = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];

      if (checked) {
        const newSelection = Array.from(
          new Set([...current, ...nodeIdsToChange])
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      } else {
        const newSelection = current.filter(
          (id) => !nodeIdsToChange.includes(id)
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      }
    });
  };

  const handleMoreIconClick = (e: React.MouseEvent<HTMLElement>) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
  };

  const handleNodeExpand = useCallback(
    async (nodeId: string) => {
      if (onNodeExpand) {
        try {
          const children = await onNodeExpand(nodeId);
          setTreeData((prevData) => {
            return updateNodeChildren(prevData, nodeId, children);
          });
        } catch (error) {
          console.error('Error loading children for node:', nodeId, error);
        }
      }
    },
    [onNodeExpand]
  );

  const updateNodeChildren = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string,
    children: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return {...node, isChildrenLoaded: true, children};
      }
      if (node.children) {
        return {
          ...node,
          children: updateNodeChildren(node.children, nodeId, children),
        };
      }
      return node;
    });
  };

  const renderNodeLabel = useCallback(
    (node: TreeViewBaseItem<ExtendedTreeItemProps>) => (
      <div
        className="location-treeitem-label"
        data-testid={`doc-explorer-left-tree-view-div-${node.id}`}
      >
        <div
          className={`location-treeitem ${node.newFolder ? 'new-input' : ''} `}
          data-testid={`doc-explorer-left-tree-view-div-wrapper-${node.id}`}
        >
          <Typography
            className="typo-text body3_size custom-text-style full-width-text"
            component="span"
            data-testid={`doc-explorer-left-tree-view-label-text-${node.id}`}
          >
            <div
              className="label-container"
              data-testid={`doc-explorer-left-tree-view-text-wrapper-${node.id}`}
            >
              <ArrTooltip content={node.label}>
                <Typography
                  className={`typo-text body3_size ${node?.disabled && 'label-disable'} ${node.isScopeSelected && 'scope-label'}`}
                  data-testid={`doc-explorer-left-tree-view-item-label-${node.id}`}
                >
                  {node.label}
                </Typography>
              </ArrTooltip>
            </div>
          </Typography>
        </div>
      </div>
    ),
    [
      isShowCheckbox,
      isShowCountLabel,
      showActions,
      handleCheckboxSelection,
      handleMoreIconClick,
      MoreMenuOptions,
      onMenuItemClick,
      hoveredNode,
    ]
  );

  const renderTree = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    const newFolders = nodes.filter(
      (node) => node.fileType === 'folder' && node.newFolder
    );
    const folders = nodes.filter(
      (node) => node.fileType === 'folder' && !node.newFolder
    );
    const files = nodes.filter((node) => node.fileType !== 'folder');
    const newArray = [...newFolders, ...folders, ...files];

    return newArray.map((node) => {
      const isSelected = isNodeSelected(node.id);
      return (
        <span
          className="tooltip-padding"
          key={node.id}
          data-testid={`doc-explorer-left-tree-view-span-${node.id}`}
        >
          <TreeItem
            sx={{
              paddingLeft: `calc(6px+var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth)})`,
            }}
            slotProps={{
              content: {
                className: `tree-item-content ${customContentClass || ''} ${node.added && 'tree-item-added'} ${isSelected && 'Mui-selected'}`,
                style: {
                  height: customContentHeight || '',
                },
              },
            }}
            key={node.id}
            itemId={node.id}
            label={renderNodeLabel(node)}
          >
            {((node.isChildrenLoaded && node.hasChildren) ||
              (node.hasChildren &&
                node.children &&
                node.children?.length > 0)) &&
              node.children &&
              renderTree(node.children)}
          </TreeItem>
        </span>
      );
    });
  };

  const findNodeById = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    id: string
  ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const findParentById = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string
  ): string | null => {
    // Recursive search through children
    for (const node of nodes) {
      if (node.children?.some((child) => child.id === nodeId)) {
        return node.id;
      }
      if (node.children) {
        const parent = findParentById(node.children, nodeId);
        if (parent) return parent;
      }
    }
    return null;
  };

  const handleVirtualRowExpand = useCallback(
    async (nodeId: string) => {
      const isExpanded = expandedIds.includes(nodeId);
      let newExpanded: string[];
      if (isExpanded) {
        newExpanded = expandedIds.filter((id) => id !== nodeId);
        if (onCollapsedNodesChange) {
          onCollapsedNodesChange([nodeId]);
        }
      } else {
        newExpanded = [...expandedIds, nodeId];
        const node = findNodeById(treeData, nodeId);
        if (node && !node?.isChildrenLoaded) {
          await handleNodeExpand(nodeId);
        }
        if (onExpandedNodesChange) {
          onExpandedNodesChange(newExpanded);
        }
      }
      setExpandedIds(newExpanded);
    },
    [
      expandedIds,
      treeData,
      handleNodeExpand,
      onExpandedNodesChange,
      onCollapsedNodesChange,
    ]
  );

  const handleExpandedItemsChange = async (
    event: React.SyntheticEvent | null,
    ids: string[]
  ) => {
    const isCtrlPressed =
      !!event &&
      ((event as React.SyntheticEvent & {ctrlKey?: boolean}).ctrlKey ||
        (
          event as React.SyntheticEvent & {
            nativeEvent?: MouseEvent | KeyboardEvent;
          }
        ).nativeEvent?.ctrlKey === true);

    if (event) {
      event.stopPropagation();
    }

    const newlyCollapsed = expandedIds.filter((id) => !ids.includes(id));
    const newlyExpanded = ids.filter((id) => !expandedIds.includes(id));

    if (isCtrlPressed) {
      let newSelection = Array.isArray(selectedItems)
        ? [...selectedItems]
        : selectedItems
          ? [selectedItems?.toString()]
          : [];

      if (newSelection.includes(newlyExpanded[0])) {
        newSelection = newSelection.filter((id) => id !== newlyExpanded[0]);
      } else {
        newSelection.push(newlyExpanded[0]);
      }
      setSelectedItems(newSelection);
    } else {
      setExpandedIds(ids);
      const currentNode = findNodeById(treeData, newlyExpanded[0]);
      if (currentNode && !currentNode?.isChildrenLoaded)
        await handleNodeExpand(currentNode?.id);

      if (onExpandedNodesChange) {
        onExpandedNodesChange(ids);
      }
      if (onCollapsedNodesChange) {
        onCollapsedNodesChange(newlyCollapsed);
      }
    }
  };

  return (
    <>
      {treeData && treeData.length > 0 ? (
        useVirtualization ? (
          <div
            className="trr-item-check"
            style={{
              height: totalHeight,
              minHeight: totalHeight,
            }}
            data-testid={`doc-explorer-left-tree-view-item-chk`}
          >
            {flattenedRows.slice(startIndex, endIndex + 1).map((row, i) => {
              const index = startIndex + i;
              const node = row.node;
              const isSelected = isNodeSelected(node.id);
              const hasChildren =
                (node.isChildrenLoaded && node.hasChildren) ||
                (node.hasChildren && node.children && node.children.length > 0);
              const isExpanded = expandedIds.includes(node.id);
              return (
                <div
                  key={node.id}
                  role="treeitem"
                  aria-expanded={hasChildren ? isExpanded : undefined}
                  className={`MuiTreeItem-content virtual-tree-content tree-item-content ${customContentClass || ''} ${node.added ? 'tree-item-added' : ''} ${isSelected ? 'Mui-selected' : ''}`}
                  style={{
                    top: index * ROW_HEIGHT_PX,
                    height: ROW_HEIGHT_PX,
                    paddingLeft: 6 + row.depth * 24,
                    ...(customContentHeight
                      ? {height: customContentHeight}
                      : {}),
                  }}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={(e) => {
                    const isExpandIcon = (e.target as HTMLElement).closest(
                      '.virtual-tree-expand-icon'
                    );
                    if (isExpandIcon && hasChildren) {
                      e.stopPropagation();
                      handleVirtualRowExpand(node.id);
                    } else if (!isShowCheckbox) {
                      handleSelectedItemsChange(e, node.id);
                    }
                  }}
                  data-testid={`doc-explorer-left-tree-view-div-${node.id}`}
                >
                  <div
                    className="virtual-tree-expand-icon MuiTreeItem-iconContainer"
                    onClick={(e) => {
                      if (hasChildren) {
                        e.stopPropagation();
                        handleVirtualRowExpand(node.id);
                      }
                    }}
                    data-testid={`doc-explorer-left-tree-view-row-expand-${node.id}`}
                  >
                    {hasChildren ? (
                      isExpanded ? (
                        <ExpandMoreIcon aria-hidden />
                      ) : (
                        <ChevronRightIcon aria-hidden />
                      )
                    ) : null}
                  </div>
                  <div
                    className="virtual-tree-label"
                    data-testid={`doc-explorer-left-tree-view-div-label-wrapper-${node.id}`}
                  >
                    {renderNodeLabel(node)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <SimpleTreeView
            className="trr-item-check"
            selectedItems={selectedItems}
            multiSelect={multipleSelection}
            onSelectedItemsChange={
              !isShowCheckbox ? handleSelectedItemsChange : undefined
            }
            itemChildrenIndentation={24}
            expansionTrigger="iconContainer"
            onExpandedItemsChange={handleExpandedItemsChange}
            expandedItems={expandedIds}
            onItemFocus={(
              event: React.SyntheticEvent | null,
              itemId: string
            ) => {
              setHoveredNode(itemId);
            }}
          >
            {renderTree(treeData)}
          </SimpleTreeView>
        )
      ) : (
        <Typography
          className="typo-text body3_size custom-text"
          data-testid={`doc-explorer-left-tree-view-no-result`}
        >
          {handleNoResultMessage}
        </Typography>
      )}
    </>
  );
};

export default DocExplorerLeftTreeView;
